import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { TNil } from '@app/app.interface';
import { ICtaForm } from '@campaign-form/campaign-form.interface';
import { CtaDetailsService } from '@campaign-form/form-fields/creators-and-ctas/cta-details.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { ExecutionChangeTrackerService } from '@core/services/execution-change-tracker.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { FormUtilitiesService } from '@utilities/form-utilities.service';
import { ILastActiveElementDetails } from '../../../cta-details.interface';
import { IGamePlayDeliverableForm } from '../../deliverable-step/deliverable-list/game-play/game-play.interface';
import { ExecutionComponent } from './execution/execution.component';
import { IExecutionForm } from './execution/execution.interface';
import { ExecutionService } from './execution/execution.service';

@Component({
  imports: [ExecutionComponent, MatButtonModule, ReactiveFormsModule, TranslateModule, IconComponent],
  selector: 'app-execution-list',
  templateUrl: './execution-list.component.html',
})
export class ExecutionListComponent {
  public readonly formArray = input.required<FormArray<FormGroup<IExecutionForm>>>();
  public readonly platformFeature = input.required<EPlatformFeature>();
  public readonly availableKpis = input.required<TExpectedKpi[] | null>();
  public readonly creatorId = input.required<string | TNil>();
  public readonly isCustomMode = input.required<boolean>();
  public readonly creators = input.required<ICreator[]>();
  public readonly ctaForm = input.required<FormGroup<ICtaForm>>();

  public readonly isAllExpanded = input<boolean>(false);
  public readonly isViewMode = input<boolean>(false);
  public readonly gamePlayValues = input<FormArray<FormGroup<IGamePlayDeliverableForm>>['value']>([]);

  public readonly executionDeleteCheckboxChange = output<MatCheckboxChange>();

  // Wave 10 follow-up - ctaForm is already threaded down here, so its own id doubles as
  // ctaTemplateId for execution events without needing a separate input from execution-step.
  public readonly ctaTemplateId = computed(() => this.ctaForm().get('id')?.value ?? undefined);

  public readonly isParentDeleteDisabled = signal<boolean>(false);
  public readonly lastActiveElementDetails = signal<Map<string, ILastActiveElementDetails>>(new Map());

  private static readonly HIGHLIGHT_NAMESPACE = 'execution';

  private readonly executionService = inject(ExecutionService);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly executionChangeTracker = inject(ExecutionChangeTrackerService);

  constructor() {
    effect(() => {
      this.checkParentDeleteDisabled();
    });
  }

  public isNewlyAddedExecution(id: string): boolean {
    return FormUtilitiesService.isNewlyHighlightedElement(id, ExecutionListComponent.HIGHLIGHT_NAMESPACE);
  }

  public get visibleExecutionCount(): number {
    return this.formArray().controls.filter(
      (c) =>
        !c.value.exclusiveTo || this.isViewMode() || (this.isCustomMode() && c.value.exclusiveTo === this.creatorId()),
    ).length;
  }

  public addExecution(): void {
    const executionForm = this.executionService
      .getConfig()
      .createForm(this.platformFeature(), undefined, this.isCustomMode() ? this.creatorId() : undefined);

    this.formArray().push(executionForm);
    this.isParentDeleteDisabled.set(this.formArray().controls.length < 2);
    this.lastActiveElementDetails.set(
      CtaDetailsService.getLastActiveElementCreatorNames(this.formArray(), this.isCustomMode(), this.creators()),
    );

    FormUtilitiesService.highlightNewElement(
      executionForm,
      ExecutionListComponent.HIGHLIGHT_NAMESPACE,
      undefined,
      500,
      true,
    );

    const executionId = executionForm.get('id')?.value ?? undefined;

    // Fires immediately (explicit "Add another" click) - marked as added so ExecutionComponent's
    // executionType-change handler doesn't defer-fire it again for this row. See
    // ExecutionChangeTrackerService's doc comment for why the step's own silently-created default
    // row (which never goes through this method) needs that deferred fallback instead.
    this.executionChangeTracker.markExecutionAdded(executionId);
    this.eventTrackingService.track(EEventType.campaignCreationExecutionAdded, {
      campaignCreationCurrentStep: 3,
      campaignCreationIsOverride: this.isCustomMode(),
      creatorId: this.isCustomMode() ? (this.creatorId() ?? undefined) : undefined,
      ctaTemplateId: this.ctaTemplateId(),
      executionId,
    });
  }

  public removeExecution(index: number): void {
    const executionId = this.formArray().at(index).get('id')?.value ?? undefined;

    this.formArray().removeAt(index);
    this.checkParentDeleteDisabled();

    this.eventTrackingService.track(EEventType.campaignCreationExecutionRemoved, {
      campaignCreationCurrentStep: 3,
      campaignCreationIsOverride: this.isCustomMode(),
      creatorId: this.isCustomMode() ? (this.creatorId() ?? undefined) : undefined,
      ctaTemplateId: this.ctaTemplateId(),
      executionId,
    });
  }

  public getExecutionNumber(index: number): number {
    const { controls } = this.formArray();
    let visibleExecutionCount = 0;

    for (let i = 0; i <= index; i += 1) {
      const control = controls[i];

      if (!control) {
        continue;
      }

      const isVisible =
        !control.value.exclusiveTo ||
        this.isViewMode() ||
        (this.isCustomMode() && control.value.exclusiveTo === this.creatorId());

      if (isVisible) {
        visibleExecutionCount += 1;
      }
    }

    return visibleExecutionCount;
  }

  public checkParentDeleteDisabled(): void {
    this.isParentDeleteDisabled.set(
      CtaDetailsService.isLastCustomCtaElement(this.formArray(), this.isCustomMode(), this.creatorId()),
    );
    this.lastActiveElementDetails.set(
      CtaDetailsService.getLastActiveElementCreatorNames(this.formArray(), this.isCustomMode(), this.creators()),
    );
  }
}
